// @file Post embeds store for eagerly fetching oEmbed data
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { createArcadeEmbedUrl } from '@@/bits/url'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { OEmbedApi } from '@@/surface/api/oembed'
import type { Cid } from '@@/types'
import { LinkHelpers } from '@padlet/beethoven-client'
import { FetchError } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface EmbedState {
  embedUrl?: string // Embed URL fetched directly from embed-url endpoint
  isLoading: boolean
  error: Error | null
}

/**
 * Store for managing embed URLs for post attachments.
 *
 * This store eagerly fetches embed URLs for Padlet embeds (walls, slideshows, etc.)
 * to improve performance by loading embeds in parallel rather than one at a time.
 *
 * Can be used in multiple contexts:
 * - Slideshows: Fetch all embeds when slideshow loads
 * - Expanded posts: Fetch embed when post is opened
 * - Post lists: Prefetch embeds for visible/nearby posts
 * - Any other context where embeds need to be displayed
 */
export const usePostEmbedsStore = defineStore('postEmbeds', () => {
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  /**
   * Map of embed data by post CID
   * Stores the fetched embed URL, loading state, and any errors
   */
  const embedStateByPostCid = ref<Record<Cid, EmbedState>>({})

  /**
   * Map of in-flight fetch promises by URL
   * Used to deduplicate requests for the same URL
   */
  const inFlightFetches = ref<Record<string, Promise<void>>>({})

  /* ---------------------- */
  /* COMPUTED               */
  /* ---------------------- */

  /**
   * Whether any embeds are currently loading
   */
  const isLoadingAnyEmbed = computed(() => {
    return Object.values(embedStateByPostCid.value).some((state) => state.isLoading)
  })

  /**
   * Number of embeds currently cached
   */
  const cachedEmbedCount = computed(() => {
    return Object.keys(embedStateByPostCid.value).length
  })

  /* ---------------------- */
  /* HELPER FUNCTIONS       */
  /* ---------------------- */

  /**
   * Forward all query params from the original URL to the embed URL.
   */
  const forwardParamsToEmbedUrl = (originalUrl: string, embedUrl: string): string => {
    const original = new URL(originalUrl)
    const embed = new URL(embedUrl)
    original.searchParams.forEach((value, key) => {
      embed.searchParams.set(key, value)
    })
    return embed.toString()
  }

  /**
   * Check if a post should use embed (has Padlet wall/slideshow/arcade attachment)
   */
  const shouldPostUseEmbed = (postCid: Cid): boolean => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (!post?.attachment) return false

    const displayAttributes = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(postCid)
    if (!displayAttributes) return false

    // Check if this is a Padlet wall, slideshow, or TA Arcade link that should use embeds
    return (
      LinkHelpers.isPadletWall(displayAttributes) ||
      LinkHelpers.isSlideshow(displayAttributes) ||
      LinkHelpers.isArcade(displayAttributes)
    )
  }

  /**
   * Get all post CIDs from an array that should use embeds
   */
  const filterPostCidsThatNeedEmbeds = (postCids: Cid[]): Cid[] => {
    return postCids.filter((cid) => shouldPostUseEmbed(cid))
  }

  /* ---------------------- */
  /* GETTER FUNCTIONS       */
  /* ---------------------- */

  /**
   * Check if oEmbed data is loading for a specific post
   */
  const isLoadingOEmbedForPost = (postCid: Cid): boolean => {
    return embedStateByPostCid.value[postCid]?.isLoading ?? false
  }

  /**
   * Get error for a specific post's oEmbed fetch
   */
  const getOEmbedErrorForPost = (postCid: Cid): Error | null => {
    return embedStateByPostCid.value[postCid]?.error ?? null
  }

  /**
   * Check if embed URL has been loaded for a post
   */
  const hasOEmbedDataForPost = (postCid: Cid): boolean => {
    const state = embedStateByPostCid.value[postCid]
    return state != null && !state.isLoading && state.embedUrl != null
  }

  /**
   * Get embed URL for a specific post
   * @param postCid - The post CID
   * @param isSlideshow - Flag to indicate if this is a slideshow (defaults to false)
   */
  const getEmbedUrlForPost = (postCid: Cid, isSlideshow: boolean = false): string | null => {
    const embedState = embedStateByPostCid.value[postCid]

    if (!embedState?.embedUrl) return null

    if (!isSlideshow) return embedState.embedUrl

    // Use URL API to safely append /slideshow path and query params,
    // avoiding malformed URLs when embedUrl already has query params (e.g. forwarded frame_id)
    const url = new URL(embedState.embedUrl)
    url.pathname = `${url.pathname}/slideshow`
    if (!url.searchParams.has('autoplay')) url.searchParams.set('autoplay', '0')
    if (!url.searchParams.has('loop')) url.searchParams.set('loop', '0')
    if (!url.searchParams.has('duration')) url.searchParams.set('duration', 'auto')
    return url.toString()
  }

  /**
   * Get embed code for a specific post
   * @param postCid - The post CID
   * @param isSlideshow - Flag to indicate if this is a slideshow (defaults to false)
   */
  const getEmbedCodeForPost = (postCid: Cid, isSlideshow: boolean = false): string | null => {
    const embedUrl = getEmbedUrlForPost(postCid, isSlideshow)
    if (!embedUrl) return null

    return `<iframe src="${embedUrl}" frameborder="0" allow="camera;microphone;geolocation;display-capture;clipboard-write" style="width:100%;height:100%;border:none;"></iframe>`
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  /**
   * Initialize the embed state for a post
   */
  const initializeEmbedState = (postCid: Cid): void => {
    if (!embedStateByPostCid.value[postCid]) {
      embedStateByPostCid.value[postCid] = {
        isLoading: false,
        error: null,
      }
    }
  }

  /**
   * Fetch embed URL for a specific post
   */
  const fetchOEmbedDataForPost = async (postCid: Cid): Promise<void> => {
    const displayAttributes = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(postCid)

    if (!displayAttributes?.url) {
      return
    }

    // For TA Arcade links, use the URL directly without fetching oEmbed data since there is no specific embed URL for Arcade links.
    // Transform the URL to add ?embed=1 parameter and remove /play suffix if present.
    if (LinkHelpers.isArcade(displayAttributes)) {
      embedStateByPostCid.value[postCid] = {
        embedUrl: createArcadeEmbedUrl(displayAttributes.url),
        isLoading: false,
        error: null,
      }
      return
    }

    // Create cache key from URL
    const cacheKey = displayAttributes.url

    // Initialize state if needed
    initializeEmbedState(postCid)

    // Check if any other post already has cached data for this URL
    const existingPostWithSameUrl = Object.keys(embedStateByPostCid.value).find((cid) => {
      const attrs = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(cid)
      if (!attrs?.url) return false
      return attrs.url === cacheKey && embedStateByPostCid.value[cid]?.embedUrl
    })

    if (existingPostWithSameUrl) {
      const cachedData = embedStateByPostCid.value[existingPostWithSameUrl]
      embedStateByPostCid.value[postCid] = {
        embedUrl: cachedData.embedUrl,
        isLoading: false,
        error: cachedData.error,
      }
      return
    }

    // Set loading state
    embedStateByPostCid.value[postCid] = {
      ...embedStateByPostCid.value[postCid],
      isLoading: true,
      error: null,
    }

    // Check if there's already a fetch in progress for this URL
    if (cacheKey in inFlightFetches.value) {
      try {
        await inFlightFetches.value[cacheKey]
        // Find the post that has the fetched data for this URL
        const sourcePostWithData = Object.keys(embedStateByPostCid.value).find((cid) => {
          const attrs = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(cid)
          if (!attrs?.url) return false
          return attrs.url === cacheKey && embedStateByPostCid.value[cid]?.embedUrl
        })

        if (sourcePostWithData) {
          // Copy the fetched data to this post's state
          const sourceData = embedStateByPostCid.value[sourcePostWithData]
          embedStateByPostCid.value[postCid] = {
            embedUrl: sourceData.embedUrl,
            isLoading: false,
            error: sourceData.error,
          }
        } else {
          // If we couldn't find the source data, clear loading state
          embedStateByPostCid.value[postCid] = {
            isLoading: false,
            error: null,
          }
        }
        return
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        // Update state with error
        embedStateByPostCid.value[postCid] = {
          isLoading: false,
          error: err,
        }
        return
      }
    }

    // Fetch embed URL directly
    const fetchPromise = OEmbedApi.fetchEmbedUrl(displayAttributes.url)
      .then((response) => {
        // Update state with embed URL, preserving forwarded query params from the original URL
        const embedUrl = forwardParamsToEmbedUrl(displayAttributes.url, response.embedUrl)
        embedStateByPostCid.value[postCid] = {
          embedUrl,
          isLoading: false,
          error: null,
        }
      })
      .catch((error) => {
        const err = error instanceof Error ? error : new Error(String(error))

        console.error(`[postEmbeds] Failed to fetch embed URL for post ${postCid}:`, err)
        captureNonNetworkFetchError(err as FetchError, {
          source: 'fetchOEmbedDataForPost',
          postCid,
        })

        // Update state with error
        embedStateByPostCid.value[postCid] = {
          isLoading: false,
          error: err,
        }

        throw err
      })
      .finally(() => {
        // Clean up the in-flight fetch cache
        delete inFlightFetches.value[cacheKey]
      })

    // Cache the promise
    inFlightFetches.value[cacheKey] = fetchPromise

    try {
      await fetchPromise
    } catch {
      // Error already handled in catch block above
    }
  }

  /**
   * Eagerly fetch embed URLs for multiple posts in parallel
   * @param postCids - Array of post CIDs to fetch embeds for
   * @param filterByEmbedType - Whether to filter only posts that need embeds (default: true)
   * @param skipCached - Whether to skip posts that already have cached embed data (default: true)
   */
  const fetchEmbedsForPosts = async (
    postCids: Cid[],
    filterByEmbedType: boolean = true,
    skipCached: boolean = true,
  ): Promise<void> => {
    if (postCids.length === 0) return

    // Filter to only posts that need embeds
    let cidsToFetch = filterByEmbedType ? filterPostCidsThatNeedEmbeds(postCids) : postCids

    // Skip posts that already have cached embed data
    if (skipCached) {
      cidsToFetch = cidsToFetch.filter((cid) => !hasOEmbedDataForPost(cid))
    }

    if (cidsToFetch.length === 0) return

    // Fetch all embed URLs in parallel
    await Promise.allSettled(cidsToFetch.map((postCid) => fetchOEmbedDataForPost(postCid)))
  }

  /**
   * Prefetch embed URLs for posts around a current post
   * Useful for loading embeds for nearby posts without loading everything
   *
   * @param allPostCids - Array of all post CIDs in order
   * @param currentPostCid - The current post CID
   * @param range - Number of posts before and after to prefetch (default: 2)
   */
  const prefetchNearbyEmbeds = async (allPostCids: Cid[], currentPostCid: Cid, range: number = 2): Promise<void> => {
    const currentIndex = allPostCids.indexOf(currentPostCid)
    if (currentIndex === -1) return

    // Get posts within range
    const startIndex = Math.max(0, currentIndex - range)
    const endIndex = Math.min(allPostCids.length - 1, currentIndex + range)
    const nearbyPostCids = allPostCids.slice(startIndex, endIndex + 1)

    // Filter to only posts that need embeds and aren't already loaded/loading
    const postsToFetch = nearbyPostCids.filter((cid) => {
      if (!shouldPostUseEmbed(cid)) return false

      const state = embedStateByPostCid.value[cid]
      return !state || (!state.embedUrl && !state.isLoading)
    })

    if (postsToFetch.length === 0) return

    await Promise.allSettled(postsToFetch.map((postCid) => fetchOEmbedDataForPost(postCid)))
  }

  /**
   * Clear all oEmbed data
   * Useful when context changes (e.g., navigating away from page)
   */
  const clearAllOEmbedData = (): void => {
    embedStateByPostCid.value = {}
  }

  /**
   * Clear oEmbed data for a specific post
   */
  const clearOEmbedDataForPost = (postCid: Cid): void => {
    if (embedStateByPostCid.value[postCid]) {
      delete embedStateByPostCid.value[postCid]
    }
  }

  /**
   * Clear oEmbed data for multiple posts
   */
  const clearOEmbedDataForPosts = (postCids: Cid[]): void => {
    postCids.forEach((cid) => clearOEmbedDataForPost(cid))
  }

  return {
    // State
    embedStateByPostCid,

    // Computed
    isLoadingAnyEmbed,
    cachedEmbedCount,

    // Helper functions
    shouldPostUseEmbed,
    filterPostCidsThatNeedEmbeds,

    // Getter functions
    isLoadingOEmbedForPost,
    getOEmbedErrorForPost,
    hasOEmbedDataForPost,
    getEmbedCodeForPost,
    getEmbedUrlForPost,

    // Actions
    fetchOEmbedDataForPost,
    fetchEmbedsForPosts,
    prefetchNearbyEmbeds,
    clearAllOEmbedData,
    clearOEmbedDataForPost,
    clearOEmbedDataForPosts,
  }
})
